import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import TopologyMap from "@/components/TopologyMap";
import TunnelsList from "@/components/TunnelsList";
import QosCharts from "@/components/QosCharts";
import FailoverSimulator from "@/components/FailoverSimulator";
import { Zap } from "lucide-react";
import { toast } from "sonner";

export default function Network() {
  const { tenant } = useTenant();
  const [sites, setSites] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!tenant) return;
    api.get(`/network/sites?tenant=${tenant.id}`).then(({ data }) => setSites(data));
  }, [tenant, refreshKey]);

  return (
    <div className="space-y-6" data-testid="network-page">
      <div className="border-b border-slate-800 pb-5 flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-mono mb-2">
            Network Operations · Connectivity & SD-WAN
          </div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Edge Fabric</h1>
          <p className="text-sm text-slate-400 mt-1">
            {sites.length || 12} units · Brisanet · Claro · Dnet · V.tal · multi-vendor SD-WAN
          </p>
        </div>
      </div>

      {/* Top: Tunnels + Failover Simulator */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-8 border border-slate-800 bg-[#0F172A]">
          <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
            <div>
              <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">SD-WAN Tunnels</h3>
              <div className="text-[10px] text-slate-500 mt-0.5">All tunnels · per remote unit · per carrier</div>
            </div>
            <span className="text-[10px] font-mono text-emerald-400 evos-blink">● LIVE</span>
          </div>
          <TunnelsList refreshKey={refreshKey} />
        </div>

        <div className="xl:col-span-4 border border-amber-500/30 bg-gradient-to-br from-[#0F172A] to-[#1c1410]">
          <div className="border-b border-amber-500/30 px-5 py-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-400" />
            <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">Link Failure Simulator</h3>
          </div>
          <FailoverSimulator onComplete={() => setRefreshKey((k) => k + 1)} />
        </div>
      </div>

      {/* QoS Charts */}
      <div className="border border-slate-800 bg-[#0F172A]">
        <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">Link Quality (QoS)</h3>
            <div className="text-[10px] text-slate-500 mt-0.5">Latency · Jitter · Packet Loss — per carrier · real-time</div>
          </div>
          <span className="text-[10px] font-mono text-emerald-400 evos-blink">● STREAMING</span>
        </div>
        <QosCharts />
      </div>

      {/* Topology */}
      <div className="border border-slate-800 bg-[#0F172A]">
        <div className="border-b border-slate-800 px-5 py-3 text-xs uppercase tracking-[0.25em] font-mono font-semibold">
          Topology · Pernambuco
        </div>
        <TopologyMap />
      </div>
    </div>
  );
}
